"use client";

import { motion, useReducedMotion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/a11y";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
interface ReviewItem {
  id: string;
  title: string;
  text: string;
  attribution: string;
}
const reviewsData: ReviewItem[] = [{
  id: "review-1",
  title: "Très bon guide d'installation",
  text: "Livre très intéressant pour ceux qui souhaiteraient s'installer en Suède, avec beaucoup d'informations et de conseils avant de sauter le pas. Merci d'avoir partager votre expérience, même si chaque expérience est différente, mais cela aide déjà énormément les personnes qui souhaiteraient s'installer en Suède sur les différentes étapes à suivre avant le départ afin de pouvoir se préparer au mieux avant le départ et jusqu'à l'installation en Suède.",
  attribution: "Avis reçu via Payhip"
}, {
  id: "review-2",
  title: "Vraiment utile",
  text: "Je recommande cet ouvrage qui permet d'avoir des éléments concrets et pratiques d'une grande aide. J'ai personnellement eu des infos que même mon mari suédois n'a pas su me donner !",
  attribution: "Avis reçu via Payhip"
}, {
  id: "review-3",
  title: "Guide très complet",
  text: "Ce guide est vraiment bien construit, il permet d'avoir une bonne vision des étapes incontournables pour s'installer en Suède. Ce ne sera pas une mince affaire, on se rend bien compte du lot de démarches à prévoir. Mais au moins, on est prévenus, et on s'y prépare!",
  attribution: "Avis reçu via Payhip"
}, {
  id: "review-4",
  title: "Incontournable",
  text: "Guide très riche, bien détaillé, avec des retours d'expérience concrète que je n'ai pas trouvé ailleurs. Merci beaucoup pour ce travail",
  attribution: "Avis reçu via Payhip"
}];
function ReviewCard({
  review
}: {
  review: ReviewItem;
}) {
  return <Card className="h-full rounded-2xl border border-border bg-card px-6 py-5 text-card-foreground shadow-xs flex flex-col justify-between gap-4">
      <div className="flex flex-col gap-3">
        <h3 className="font-serif text-lg font-bold text-foreground leading-snug">
          {review.title}
        </h3>
        <p className="text-foreground/85 text-sm sm:text-base leading-relaxed">
          {review.text}
        </p>
      </div>
      <div className="pt-2 border-t border-border/50">
        <span className="font-mono text-xs text-muted-foreground block">
          {review.attribution}
        </span>
      </div>
    </Card>;
}
export default function CustomerReviews() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="customerreviews" data-nav="light" className="bg-muted text-foreground py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={containerVariants} className="flex flex-col items-center text-center mb-12 md:mb-16">
          <Badge variant="outline" className="uppercase tracking-widest text-secondary border-secondary/30 mb-4 px-3 py-1 font-semibold text-xs">
            Avis vérifiés
          </Badge>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal text-foreground tracking-tight max-w-2xl">
            Ce que les lecteurs en pensent
          </h2>
          <div className="h-0.5 w-16 bg-secondary/60 mt-4 rounded-full" aria-hidden="true" />
        </motion.div>

        {/* Desktop Layout (md+) */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={containerVariants} className="hidden md:grid md:grid-cols-2 gap-6 lg:gap-8 items-stretch">
          {reviewsData.map((review, i) => <div key={review.id} data-index={i} className="h-full">
              <ReviewCard review={review} />
            </div>)}
        </motion.div>

        {/* Mobile Carousel (<md) */}
        <div className="block md:hidden">
          <Swiper modules={[A11y]} slidesPerView={1.15} spaceBetween={16} grabCursor={true} a11y={{
          prevSlideMessage: "Avis précédent",
          nextSlideMessage: "Avis suivant"
        }} className="overflow-visible pb-4">
            {reviewsData.map((review, i) => <SwiperSlide key={review.id} data-index={i} className="h-auto">
                <ReviewCard review={review} />
              </SwiperSlide>)}
          </Swiper>
        </div>
      </div>
    </section>;
}